import React, { useState, useMemo, useCallback } from "react";
import {
    Box,
    Typography,
    Paper,
    Button,
    IconButton,
    Tooltip,
    Snackbar,
    Alert,
    TextField,
    InputAdornment,
    Fab,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions,
    Stack,
    ToggleButtonGroup,
    ToggleButton,
    Chip,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import {
    Edit as EditIcon,
    Add as AddIcon,
    Delete as DeleteIcon,
    Search as SearchIcon,
    Clear as ClearIcon,
    InsertDriveFile as FileIcon,
    Person as PersonIcon,
    DirectionsCar as CarIcon,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";

import ProjectsForm from "./ProjectsForm";
import { useProjects } from "../../hooks/useProjects";
import { useOrganizations } from "../../hooks/useOrganizations";
import { useAuthContext } from "../../context/AuthProvider";

const ProjectsDashboard = () => {
    const navigate = useNavigate();
    const [open, setOpen] = useState(false);
    const [editProject, setEditProject] = useState(null);
    const [alert, setAlert] = useState({ open: false, message: "", severity: "success" });
    const [search, setSearch] = useState("");
    const [statusFilter, setStatusFilter] = useState("ALL");

    const [openConfirm, setOpenConfirm] = useState(false);
    const [selectedProject, setSelectedProject] = useState(null);

    const { user } = useAuthContext();
    const { projects = [], isLoading, error, createMutation, updateMutation, deleteMutation } = useProjects();
    const { organizations = [] } = useOrganizations();

    // Mapa de organizaciones para acceso rápido
    const orgMap = useMemo(() => {
        return new Map(organizations.map((org) => [org.orgid, org.orgnom]));
    }, [organizations]);

    const showAlert = useCallback((message, severity = "success") => {
        setAlert({ open: true, message, severity });
    }, []);

    const handleOpenCreate = useCallback(() => {
        setEditProject(null);
        setOpen(true);
    }, []);

    const handleOpenEdit = useCallback((project) => {
        setEditProject(project);
        setOpen(true);
    }, []);

    const handleCloseForm = useCallback(() => {
        setOpen(false);
        setEditProject(null);
    }, []);

    const handleOpenConfirm = useCallback((project) => {
        setSelectedProject(project);
        setOpenConfirm(true);
    }, []);

    const handleCloseConfirm = useCallback(() => {
        setOpenConfirm(false);
        setSelectedProject(null);
    }, []);

    const handleSubmitProject = useCallback(
        (data) => {
            if (!user) {
                showAlert("Debe iniciar sesión para realizar esta acción", "error");
                return;
            }

            if (editProject) {
                updateMutation.mutate(
                    { id: editProject.proid, data: { ...data, updbyid: user.EmpID } },
                    {
                        onSuccess: () => {
                            showAlert("Proyecto actualizado con éxito");
                            handleCloseForm();
                        },
                        onError: (err) => {
                            showAlert(err?.message || "Error al actualizar el proyecto", "error");
                        },
                    }
                );
            } else {
                createMutation.mutate(
                    { ...data, crebyid: user.EmpID },
                    {
                        onSuccess: () => {
                            showAlert("Proyecto creado con éxito");
                            handleCloseForm();
                        },
                        onError: (err) => {
                            showAlert(err?.message || "Error al crear el proyecto", "error");
                        },
                    }
                );
            }
        },
        [user, editProject, updateMutation, createMutation, showAlert, handleCloseForm]
    );

    const handleConfirmDelete = useCallback(() => {
        if (!selectedProject) return;

        deleteMutation.mutate(selectedProject.proid, {
            onSuccess: () => {
                showAlert("Proyecto eliminado con éxito");
                handleCloseConfirm();
            },
            onError: (err) => {
                showAlert(err?.message || "Error al eliminar el proyecto", "error");
                handleCloseConfirm();
            },
        });
    }, [selectedProject, deleteMutation, showAlert, handleCloseConfirm]);

    // Navegación a rutas hijas con datos del proyecto
    const navigateToEmployees = useCallback(
        (project) => {
            navigate(`/administracion/proyecto/${project.proid}/employees`, {
                state: {
                    pronom: project.pronom,
                    orgnom: orgMap.get(project.orgid) || "N/A",
                },
            });
        },
        [navigate, orgMap]
    );

    const navigateToVehicles = useCallback(
        (project) => {
            navigate(`/administracion/proyecto/${project.proid}/vehicles`, {
                state: {
                    pronom: project.pronom,
                    orgnom: orgMap.get(project.orgid) || "N/A",
                },
            });
        },
        [navigate, orgMap]
    );

    const navigateToDocuments = useCallback(
        (project) => {
            navigate(`/administracion/proyectos/${project.proid}/documents`, {
                state: {
                    pronom: project.pronom,
                    orgnom: orgMap.get(project.orgid) || "N/A",
                },
            });
        },
        [navigate, orgMap]
    );

    // Filtros y búsqueda
    const normalizedSearch = search.trim().toLowerCase();

    const rows = useMemo(() => {
        let data = projects;

        if (statusFilter === "A") {
            data = data.filter((p) => p.estreg === "A");
        } else if (statusFilter === "NOT_A") {
            data = data.filter((p) => p.estreg !== "A");
        }

        if (normalizedSearch) {
            data = data.filter((p) => {
                const pronom = (p.pronom || "").toLowerCase();
                const orgnom = (orgMap.get(p.orgid) || "").toLowerCase();
                return pronom.includes(normalizedSearch) || orgnom.includes(normalizedSearch);
            });
        }

        return data;
    }, [projects, statusFilter, normalizedSearch, orgMap]);

    const columns = useMemo(
        () => [
            {
                field: "pronom",
                headerName: "Nombre",
                flex: 1,
                minWidth: 200,
                valueGetter: (params) => params.row?.pronom || "—",
            },
            {
                field: "orgid",
                headerName: "Organización",
                flex: 1,
                minWidth: 180,
                valueGetter: (params) => orgMap.get(params.value) || "N/A",
            },
            {
                field: "estreg",
                headerName: "Estado",
                width: 120,
                renderCell: (params) => {
                    const isActive = params.value === "A";
                    return (
                        <Chip
                            label={isActive ? "ACTIVO" : "INACTIVO"}
                            color={isActive ? "success" : "warning"}
                            size="small"
                            variant="outlined"
                        />
                    );
                },
            },
            {
                field: "actions",
                headerName: "Acciones",
                width: 200,
                sortable: false,
                filterable: false,
                align: "center",
                headerAlign: "center",
                renderCell: (params) => (
                    <Stack direction="row" spacing={0.5} justifyContent="center">
                        <Tooltip title="Editar">
                            <IconButton size="small" onClick={() => handleOpenEdit(params.row)}>
                                <EditIcon color="primary" fontSize="small" />
                            </IconButton>
                        </Tooltip>

                        <Tooltip title="Documentos">
                            <IconButton size="small" onClick={() => navigateToDocuments(params.row)}>
                                <FileIcon sx={{ color: "#9c27b0", fontSize: 20 }} />
                            </IconButton>
                        </Tooltip>

                        <Tooltip title="Conductores">
                            <IconButton size="small" onClick={() => navigateToEmployees(params.row)}>
                                <PersonIcon sx={{ color: "#009688", fontSize: 20 }} />
                            </IconButton>
                        </Tooltip>

                        <Tooltip title="Vehículos">
                            <IconButton size="small" onClick={() => navigateToVehicles(params.row)}>
                                <CarIcon color="warning" fontSize="small" />
                            </IconButton>
                        </Tooltip>

                        <Tooltip title="Eliminar">
                            <IconButton
                                size="small"
                                onClick={() => handleOpenConfirm(params.row)}
                                disabled={deleteMutation.isPending}
                            >
                                <DeleteIcon color="error" fontSize="small" />
                            </IconButton>
                        </Tooltip>
                    </Stack>
                ),
            },
        ],
        [
            orgMap,
            handleOpenEdit,
            handleOpenConfirm,
            deleteMutation.isPending,
            navigateToDocuments,
            navigateToEmployees,
            navigateToVehicles,
        ]
    );

    if (error) {
        return (
            <Paper sx={{ p: 3 }}>
                <Typography color="error">Error al cargar proyectos: {error.message}</Typography>
            </Paper>
        );
    }

    return (
        <Paper
            sx={{
                p: 2.5,
                borderRadius: 2,
                height: "100vh",
                display: "flex",
                flexDirection: "column",
                backgroundColor: "#f9f9f9",
            }}
        >
            <Stack spacing={2} sx={{ flex: 1, minHeight: 0 }}>
                <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={1}>
                    <Typography variant="h5" fontWeight={700}>
                        Proyectos
                    </Typography>

                    <ToggleButtonGroup
                        size="small"
                        exclusive
                        value={statusFilter}
                        onChange={(_, val) => val && setStatusFilter(val)}
                    >
                        <ToggleButton value="ALL">Todos</ToggleButton>
                        <ToggleButton value="A">Activos</ToggleButton>
                        <ToggleButton value="NOT_A">Inactivos</ToggleButton>
                    </ToggleButtonGroup>
                </Box>

                <TextField
                    size="small"
                    label="Buscar por nombre del proyecto u organización..."
                    fullWidth
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon fontSize="small" />
                            </InputAdornment>
                        ),
                        endAdornment: search ? (
                            <InputAdornment position="end">
                                <Tooltip title="Limpiar">
                                    <IconButton size="small" onClick={() => setSearch("")}>
                                        <ClearIcon fontSize="small" />
                                    </IconButton>
                                </Tooltip>
                            </InputAdornment>
                        ) : null,
                    }}
                />

                <Box sx={{ flex: 1, minHeight: 0, width: "100%" }}>
                    <DataGrid
                        rows={rows}
                        columns={columns}
                        loading={isLoading}
                        getRowId={(row) => row.proid}
                        disableRowSelectionOnClick
                        pageSizeOptions={[15, 20, 25, 50, 100]}
                        initialState={{
                            pagination: { paginationModel: { pageSize: 20, page: 0 } },
                            sorting: { sortModel: [{ field: "pronom", sort: "asc" }] },
                        }}
                        sx={{
                            borderRadius: 2,
                            backgroundColor: "#fff",
                            "& .MuiDataGrid-columnHeaders": {
                                fontWeight: 700,
                                backgroundColor: "#f0f0f0",
                            },
                        }}
                    />
                </Box>
            </Stack>

            <Fab
                color="primary"
                aria-label="add"
                sx={{ position: "fixed", bottom: 20, right: 20 }}
                onClick={handleOpenCreate}
            >
                <AddIcon />
            </Fab>

            {open && (
                <ProjectsForm
                    open={open}
                    handleClose={handleCloseForm}
                    onSubmit={handleSubmitProject}
                    defaultValues={editProject || {}}
                    organizations={organizations} // Pasamos las organizaciones al formulario para evitar otra carga
                />
            )}

            <Dialog open={openConfirm} onClose={handleCloseConfirm}>
                <DialogTitle>Confirmar eliminación</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        ¿Está seguro de que desea eliminar el proyecto{" "}
                        <strong>{selectedProject?.pronom}</strong>? Esta operación no se puede deshacer.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseConfirm} color="inherit">
                        Cancelar
                    </Button>
                    <Button
                        onClick={handleConfirmDelete}
                        variant="contained"
                        color="error"
                        disabled={deleteMutation.isPending}
                    >
                        {deleteMutation.isPending ? "Eliminando..." : "Eliminar"}
                    </Button>
                </DialogActions>
            </Dialog>

            <Snackbar
                open={alert.open}
                autoHideDuration={4000}
                onClose={() => setAlert((a) => ({ ...a, open: false }))}
                anchorOrigin={{ vertical: "top", horizontal: "right" }}
            >
                <Alert
                    onClose={() => setAlert((a) => ({ ...a, open: false }))}
                    severity={alert.severity}
                    variant="filled"
                    sx={{ width: "100%" }}
                >
                    {alert.message}
                </Alert>
            </Snackbar>
        </Paper>
    );
};

export default ProjectsDashboard;